import { h, Fragment } from 'preact';
import { useState, useMemo, useCallback, useEffect } from 'preact/hooks';
import Image from 'utils/image';
import Badge from 'utils/badge';
import CustomizerIcon from 'utils/customizer-icon';
import Swatches from 'utils/swatches';
import Price from '../product/price';

const getCurrentVariant = (saleCollection, product) => {
  if (saleCollection === 'true') {
    return product.variants.find((variant) => variant.saleVariant === 'true');
  }
  return product.variants.find((variant) => variant.saleVariant !== 'true');
};

const ProductCard = ({ product, openCustomizerStyleModal, saleCollection, layout, template, ...props }) => {
  const [currentVariant, setCurrentVariant] = useState(
    template === 'collection' ? getCurrentVariant(saleCollection, product) : product.variants[0],
  );

  const [filteredVariants, setFilteredVariants] = useState(product.variants);

  // Function to get variant images based on the color option
  const getVariantImages = (colorOption) => {
    const variantWithMatchingColor = product.variants.find((v) => {
      const vColorOption = v.selectedOptions?.find((o) => o.name.toLowerCase() === 'color')?.value;
      return vColorOption === colorOption && v.images.length > 0;
    });
    return variantWithMatchingColor?.images || [];
  };

  useEffect(() => {
    if (template === 'collection') {
      const newVariants = filterVariantsBySale(product.variants, saleCollection === 'true');
      setFilteredVariants(newVariants.length > 0 ? newVariants : []);
    }
  }, []);

  const isDesktop = layout.isAboveBreakpoint('lg');
  const mainImage = currentVariant?.images?.[0] || product.images?.[0];
  const secondaryImage = currentVariant?.images?.[1] || null;

  const filterVariantsBySale = useCallback((variants, isSaleProduct) => {
    const hasSaleVariants = variants?.some((variant) => variant.saleVariant !== false);
    if (!hasSaleVariants && isSaleProduct) return [];
    return isSaleProduct
      ? variants.filter((variant) => variant.saleVariant === 'true')
      : variants.filter((variant) => variant.saleVariant !== 'true');
  }, []);

  if (!currentVariant || filteredVariants.length < 1) return null;

  const badgeData = product.badge
    ? window.domaine.productBadges?.badges.find((badge) => badge.handle === product.badge)
    : null;

  const handleVariantChange = (e, variantId, colorOption = null) => {
    const variant = product.variants.find((v) => v.id === variantId) || product.variants[0];
    const images = getVariantImages(colorOption.value);
    setCurrentVariant({ ...variant, images });
  };

  const swatches = useMemo(
    () => (
      <Swatches
        product={product}
        currentVariant={currentVariant}
        setCurrentVariant={setCurrentVariant}
        handleVariantChange={handleVariantChange}
        condensed={true}
        isDesktop={isDesktop}
        template={template}
        filteredVariants={filteredVariants}
      />
    ),
    [isDesktop, filteredVariants],
  );

  const isCustomizable =
    product.tags?.some((tag) => tag === 'is_customizable::True') &&
    !document.body.classList.contains('loop-returns-activated');
  const isKidsGraphic = product.tags?.some((tag) => tag === 'is_kids_graphic::True');
  const isHoodieGraphic = product.tags?.some((tag) => tag === 'is_hoodie_graphic::True');
  const isTankGraphic = product.tags?.some((tag) => tag === 'is_tank_graphic::True');
  const isDisableCustomizer = product.tags?.some((tag) => tag === 'is_disable_customizer::True');
  const customizerBackgroundImageTag = product.tags?.find((tag) => tag.startsWith('customizer_background_image::'));
  const customizerBackgroundImage = customizerBackgroundImageTag
    ? customizerBackgroundImageTag.split('::')[1].trim()
    : product.customizerBackgroundImageUrl?.reference?.image?.url;

  const graphicData = {
    gan: product.gan?.value,
    title: product.title,
    isKidsGraphic,
    isHoodieGraphic,
    isTankGraphic,
    customizerBackgroundImage,
  };

  const imageClass = 'group relative mb-16 block aspect-square rounded-lg border border-grey-5 no-underline';
  const titleClass = 'body-14-med lg:body-16-med mb-6 block text-primary lg:pr-40';
  const imageClassCustomizer = `${imageClass} customizer-product-card`;
  const titleClassCustomizer = `${titleClass} customizer-product-card`;

  return (
    <article className="relative flex w-full flex-col">
      {isCustomizable && !isDisableCustomizer && props.collectionHandle !== 'customizer' && (
        <CustomizerIcon
          classes={'right-[5px] top-[5px]'}
          graphicData={graphicData}
          openCustomizerStyleModal={openCustomizerStyleModal}
        />
      )}
      <a
        aria-label="Product image"
        data-gan={product?.gan?.value || product?.master_id?.value}
        className={props.collectionHandle !== 'customizer' ? imageClass : imageClassCustomizer}
        href={
          props.collectionHandle !== 'customizer'
            ? `/products/${product.handle}?variant=${currentVariant.id.split('/ProductVariant/')[1]}${saleCollection === 'true' ? `&sale_view=true` : ''}`
            : '#customizer'
        }
        data-customizer-background-img-url={
          props.collectionHandle === 'customizer' ? customizerBackgroundImage : undefined
        }
        data-is-custom-product={props.collectionHandle === 'customizer' ? 'true' : undefined}
        data-graphic-name={props.collectionHandle === 'customizer' ? product.title : undefined}
        data-is-kids-graphic={props.collectionHandle === 'customizer' ? isKidsGraphic : undefined}
        data-is-hoodie-graphic={props.collectionHandle === 'customizer' ? isHoodieGraphic : undefined}
        data-is-tank-graphic={props.collectionHandle === 'customizer' ? isTankGraphic : undefined}
      >
        {badgeData && (
          <Badge
            badge={badgeData}
            classes={'left-[10px] top-[10px]'}
          />
        )}
        {secondaryImage ? (
          <Fragment>
            <span className="flex h-full items-center justify-center">
              <Image
                {...mainImage}
                srcWidths={{ sm: 640 }}
                classes="object-contain h-full group-hover:opacity-0 rounded-lg p-15 lg:p-28 transition-opacity duration-250"
                alt={mainImage.alt}
              />
            </span>
            {secondaryImage && (
              <span className="duration-250 absolute inset-0 overflow-hidden opacity-0 transition-opacity group-hover:opacity-100">
                <Image
                  {...secondaryImage}
                  srcWidths={{ sm: 640 }}
                  classes="object-contain h-full rounded-lg"
                  alt={secondaryImage.alt}
                />
              </span>
            )}
          </Fragment>
        ) : (
          <Fragment>
            <span className="flex h-full items-center justify-center">
              <Image
                {...mainImage}
                srcWidths={{ sm: 640 }}
                classes="object-contain h-full rounded-lg p-15"
              />
            </span>
          </Fragment>
        )}
      </a>
      {props.collectionHandle !== 'customizer' && swatches}
      {product.category && <span className="body-14 mb-6 inline-block text-secondary">{product.category}</span>}
      <a
        aria-label="Product title"
        data-gan={product.gan?.value ? product.gan?.value : ''}
        className={props.collectionHandle !== 'customizer' ? titleClass : titleClassCustomizer}
        href={
          props.collectionHandle !== 'customizer'
            ? `/products/${product.handle}?variant=${currentVariant.id.split('/ProductVariant/')[1]}${saleCollection === 'true' ? `&sale_view=true` : ''}`
            : '#customizer'
        }
        data-customizer-background-img-url={
          props.collectionHandle === 'customizer' ? customizerBackgroundImage : undefined
        }
        data-is-custom-product={props.collectionHandle === 'customizer' ? 'true' : undefined}
        data-graphic-name={props.collectionHandle === 'customizer' ? product.title : undefined}
        data-is-kids-graphic={props.collectionHandle === 'customizer' ? isKidsGraphic : undefined}
        data-is-hoodie-graphic={props.collectionHandle === 'customizer' ? isHoodieGraphic : undefined}
        data-is-tank-graphic={props.collectionHandle === 'customizer' ? isTankGraphic : undefined}
      >
        {product.title}
      </a>
      {props.collectionHandle !== 'customizer' && (
        <Price
          product={product}
          currentVariant={currentVariant}
        />
      )}
    </article>
  );
};

export default ProductCard;
